/**
 * @graphql cc
 *
 * Shared hook to fetch comment author (user) data by id. Used by CommentAuthor for
 * avatar/displayName and by useCurrentUserAvatarReady to check if current user data is ready.
 */
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useCommentAuthorQuery$data,
	useCommentAuthorQuery$variables,
} from './__generated__/useCommentAuthorQuery.graphql.ts';

export const useCommentAuthorQueryDocument: GraphQLTaggedNode = graphql`
	query useCommentAuthorQuery($id: ID!) {
		user: confluence_atlassianUser(id: $id) {
			id
			displayName
			photos {
				value
			}
		}
	}
`;

export type UseCommentAuthorQueryOptions = {
	skip?: boolean;
	fetchPolicy?: 'cache-first' | 'cache-and-network';
};

export type UseCommentAuthorQueryResult = {
	data: useCommentAuthorQuery$data | undefined;
	loading: boolean;
	/** True when data is available or query is skipped; use to avoid flash before rendering author. */
	isReady: boolean;
};

/**
 * Fetches user/author data by id. Share this hook between CommentAuthor and the
 * "avatar ready" check so both use the same query and cache.
 */
export const useCommentAuthorQuery = (
	userId: string | null | undefined,
	options: UseCommentAuthorQueryOptions = {},
): UseCommentAuthorQueryResult => {
	const { skip = !userId, fetchPolicy = 'cache-and-network' } = options;

	const { data, loading } = useQueryInterop<
		useCommentAuthorQuery$data,
		useCommentAuthorQuery$variables
	>(useCommentAuthorQueryDocument, {
		variables: { id: userId! },
		fetchPolicy,
		skip: skip || !userId,
	});

	const isReady = skip || !!data || !loading;

	return {
		data,
		loading,
		isReady,
	};
};
